import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { learnApi, triggerBlobDownload } from '../../api/learnApi';

/**
 * Feature 022 — the two bulk-download buttons for the Precision Notes.
 * Each fetches the corresponding ZIP (all chapters' Word / PDF documents) and saves it.
 *
 * Rendered as rows of the Study's all-chapters tools (`ChapterTools`), which is now the single
 * place these are offered from: the tab rail, the phone picker and the Study landing sheet all
 * draw on that one component.
 */
export function PrecisionNotesDownload() {
  const [busy, setBusy] = useState<'word' | 'pdf' | null>(null);

  const download = async (kind: 'word' | 'pdf') => {
    setBusy(kind);
    try {
      const { blob, filename } = await learnApi.downloadDocumentsZip(kind);
      triggerBlobDownload(blob, filename);
    } finally {
      setBusy(null);
    }
  };

  // Matches the chapter tabs beside them: same 22px gutter, same 13.5px, same hover wash, and a
  // 44px floor so the row stays a touch target on a phone.
  const buttonClasses =
    'w-full flex items-center gap-2 min-h-[44px] px-[22px] py-1.5 text-left text-[13.5px] ' +
    'text-ink hover:bg-[#fff8e6] focus:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-inset focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-default';

  return (
    <div>
      <button type="button" onClick={() => download('word')} disabled={busy !== null} className={buttonClasses}>
        <ArrowDownTrayIcon className="h-5 w-5 shrink-0 text-ink-soft" aria-hidden="true" />
        {busy === 'word' ? 'Preparing…' : 'Download Word documents'}
      </button>
      <button type="button" onClick={() => download('pdf')} disabled={busy !== null} className={buttonClasses}>
        <ArrowDownTrayIcon className="h-5 w-5 shrink-0 text-ink-soft" aria-hidden="true" />
        {busy === 'pdf' ? 'Preparing…' : 'Download PDFs'}
      </button>
    </div>
  );
}
